import { useMutation, useQuery } from '@tanstack/react-query'
import { showNotification } from '@mantine/notifications'
import { useClient, type OrpcClient } from './useClient'
import { logError } from '@/utils/errorUtils'

// What staff should check before filing a fax (PD-2049): per group of pages and its chart, the
// pages whose OCR read names someone else. A group without `pages` is the whole fax.
export type FaxPatientCheck = Awaited<ReturnType<OrpcClient['checkIncomingFaxPatients']>>
export type FaxPatientCheckGroup = Parameters<
  OrpcClient['checkIncomingFaxPatients']
>[0]['groups'][number]

// Undefined while loading, when disabled, and after a failed read: a check that can't run never
// stands in the way of filing.
export function useFaxPatientCheck(
  taskId: string,
  groups: FaxPatientCheckGroup[],
  enabled: boolean,
) {
  const client = useClient()

  return useQuery({
    queryKey: ['fax-patient-check', taskId, groups],
    queryFn: async (): Promise<FaxPatientCheck> => {
      try {
        return await client.checkIncomingFaxPatients({ id: taskId, groups })
      } catch (err) {
        logError('fax', 'Failed to check the patients on this fax', err)
        throw err
      }
    },
    enabled: enabled && groups.length > 0,
    retry: false,
  })
}

// Records that staff confirmed filing what the check flagged (PD-2053): who, when and what, as a
// note on the fax. Run right before filing; a failure stops the filing.
export function useFaxPatientReview(taskId: string) {
  const client = useClient()

  return useMutation({
    mutationFn: (groups: FaxPatientCheckGroup[]) =>
      client.reviewIncomingFaxPatients({ id: taskId, groups }),
    onError: (error) => {
      showNotification({
        color: 'red',
        title: 'Error',
        message: error.message || 'Failed to record your review of this fax.',
      })
    },
  })
}
